import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export function useUserCredits() {
  const { user } = useAuth();
  const [balance, setBalance] = useState<number>(0);
  const [loading, setLoading] = useState(true);

  const userId = user?.id;

  useEffect(() => {
    if (!userId) { setBalance(0); setLoading(false); return; }

    let cancelled = false;

    const fetchBalance = async () => {
      const { data } = await supabase
        .from('user_credit_wallets')
        .select('balance')
        .eq('user_id', userId)
        .maybeSingle();

      if (!cancelled) {
        setBalance(data?.balance ?? 0);
        setLoading(false);
      }
    };

    setLoading(true);
    fetchBalance();

    // Realtime subscription
    const channel = supabase
      .channel(`user-credits-${userId.slice(-8)}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'user_credit_wallets', filter: `user_id=eq.${userId}` },
        (payload) => {
          const row = payload.new as any;
          if (row?.balance !== undefined) {
            setBalance(row.balance);
          }
        }
      )
      .subscribe((status) => { if (status === 'SUBSCRIBED') fetchBalance(); });

    // Focus / visibility self-heal.
    let focusTimer: ReturnType<typeof setTimeout> | null = null;
    const runDebounced = () => {
      if (focusTimer) return;
      focusTimer = setTimeout(() => { focusTimer = null; fetchBalance(); }, 500);
    };
    const onFocus = () => runDebounced();
    const onVis = () => { if (document.visibilityState === 'visible') runDebounced(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
      if (focusTimer) clearTimeout(focusTimer);
    };
  }, [userId]);


  return { balance, loading };
}
